import { useCallback, useRef, useState } from 'react'
import { AlertDialog } from '@firstbite/ui'

export interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Style the confirm action as destructive red (default true — these are deletes). */
  destructive?: boolean
}

/**
 * Promise-based confirm on the fb-ui `AlertDialog` — a drop-in replacement for
 * `window.confirm` for destructive actions on the entity detail pages, so a
 * delete asks in a real DS modal instead of the browser's native dialog.
 *
 *   const { confirm, dialog } = useConfirmDialog()
 *   const remove = async () => {
 *     if (!(await confirm({ title: 'Delete this note?' }))) return
 *     …delete…
 *   }
 *   return (<>…{dialog}</>)
 *
 * Render `dialog` once anywhere in the component; `confirm(...)` resolves `true`
 * on Confirm, `false` on Cancel.
 */
export function useConfirmDialog() {
  const [open, setOpen] = useState(false)
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolverRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback((options: ConfirmOptions) => {
    // Settle any still-pending confirm (cancelled) so its awaiter can't hang if
    // a new confirm opens before the previous one was answered.
    resolverRef.current?.(false)
    setOpts(options)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
    })
  }, [])

  const settle = useCallback((result: boolean) => {
    resolverRef.current?.(result)
    resolverRef.current = null
    setOpen(false)
  }, [])

  const dialog = opts ? (
    <AlertDialog
      isOpen={open}
      // Cancel / backdrop / Escape all resolve false; onConfirm resolves true.
      onOpenChange={(next) => {
        if (!next) settle(false)
      }}
      onConfirm={() => settle(true)}
      title={opts.title}
      description={opts.description}
      confirmLabel={opts.confirmLabel ?? 'Delete'}
      cancelLabel={opts.cancelLabel ?? 'Cancel'}
      destructive={opts.destructive ?? true}
    />
  ) : null

  return { confirm, dialog }
}
